import { useState, useEffect, useRef } from "react";
import { getRequest, postRequest, showMessage } from "../../../../config";
import ReactTable from "react-table-6";
import "react-table-6/react-table.css";
import { useDispatch } from "react-redux";
import { toggleModal } from "../../../../reducers/modalReducer";
import moment from "moment";
import AdminCompanyDetailsModal from "../../../../components/modals/AdminCompanyDetailsModal/index";
import SubscriptionPaymentConfirm from "../../../../components/modals/SubscriptionPaymentConfirm/SubscriptionPaymentConfirm";
import TagDropdown from "./TagDropdown";

const LoginHistoryCell = ({ company }) => {
    const [pos, setPos] = useState(null);
    const ref = useRef(null);
    const history = company['creatorId']?.['loginHistory'] || [];
    const lastLogin = company['creatorId']?.['lastLogin'] || company['creatorId']?.['lastLoginAt'];
    const label = lastLogin ? moment(lastLogin).format('DD/MMM/YY') : '-';

    useEffect(() => {
        if (!pos) return;
        const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setPos(null); };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [pos]);

    const handleOpen = (e) => {
        if (!history.length) return;
        const rect = e.currentTarget.getBoundingClientRect();
        setPos(pos ? null : { top: rect.bottom + 4, left: rect.left });
    };

    return (
        <div className="df jcc aic" ref={ref}>
            <button onClick={handleOpen} data-tooltip="See Login History" style={{ backgroundColor: "transparent", border: "none", cursor: history.length > 0 ? "pointer" : "default", opacity: history.length > 0 ? 1 : 0.3 }}>
                <i className="fa fa-clock-o" style={{ fontSize: '20px', color: "#038589" }} />
            </button>
            {pos && (
                <div className="bg1 shadow br10px" style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 99999, minWidth: '180px', padding: '10px 0', border: '1px solid #DDE2F2' }}>
                    <p className="fs12px fw600 c15 tac" style={{ padding: '4px 16px 8px', borderBottom: '1px solid #DDE2F2', margin: '0 0 4px' }}>Login History</p>
                    <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                        {[...history].reverse().map((entry, i) => {
                            const date = entry?.['date'] || entry;
                            const user = entry?.['userId'];
                            const name = user?.['firstName'] ? `${user['firstName']} ${user['lastName'] || ''}`.trim() : 'Unknown';
                            return (
                                <div key={i} style={{ padding: '6px 16px', borderBottom: i < history.length - 1 ? '1px solid #f0f0f8' : 'none' }}>
                                    <p className="fs12px fw600 c15" style={{ margin: '0 0 2px' }}>{name}</p>
                                    <p className="fs11px c12" style={{ margin: 0 }}>{moment(date).format('DD MMM YY, hh:mm A')} &nbsp;·&nbsp; {moment(date).fromNow()}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

const SubscribedCompaniesTab = ({ isArchived, isYopmail, setDeleteCompany, handleRevenueLeadMode }) => {
    const [data, setData] = useState([]);
    const [pages, setPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const searchTermRef = useRef("");
    const fetchIdRef = useRef(0);
    const dispatch = useDispatch();
    const [plan, setPlan] = useState('Pro');
    const [selectedCompany, setSelectedCompany] = useState(null);

    // Tag-related state
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [showTagDropdown, setShowTagDropdown] = useState(false);
    const [allTags, setAllTags] = useState([]);
    const [tableState, setTableState] = useState({ page: 0, pageSize: 20, filtered: [], sorted: [] });

    // Tag filter state
    const [filterTags, setFilterTags] = useState([]);
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);
    const [filterSearchTerm, setFilterSearchTerm] = useState("");

    // Filter tags based on search term
    const filteredFilterTags = allTags.filter(tag => tag.toLowerCase().includes(filterSearchTerm.toLowerCase()));

    // Fetch all tags on mount
    useEffect(() => {
        fetchAllTags();
    }, []);

    const fetchAllTags = async () => {
        try {
            const response = await postRequest('/company/manageTags', { action: 'list' });
            if (response?.code === 200) {
                setAllTags(response.body?.tags || []);
            }
        } catch (error) {
            console.error('Error fetching tags:', error);
        }
    };

    // Handle select all checkbox
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedCompanies(data.map(company => company._id));
        } else {
            setSelectedCompanies([]);
        }
    };

    // Handle individual checkbox
    const handleSelectCompany = (companyId) => {
        setSelectedCompanies(prev => {
            if (prev.includes(companyId)) {
                return prev.filter(id => id !== companyId);
            } else {
                return [...prev, companyId];
            }
        });
    };

    // Handle tag operations
    const handleTagAction = async (action, tags, keepOpen = false) => {
        if (selectedCompanies.length === 0) return;

        try {
            const response = await postRequest('/company/manageTags', {
                action,
                companyIds: selectedCompanies,
                tags
            });

            if (response?.code === 200) {
                // Refresh data
                fetchData(tableState);
                fetchAllTags();
                // Only close if not keepOpen
                if (!keepOpen) {
                    setShowTagDropdown(false);
                }
            }
        } catch (error) {
            console.error('Error managing tags:', error);
        }
    };

    const companiesColumns = [
        // Checkbox column
        {
            id: 'select',
            Header: () => (
                <input
                    type="checkbox"
                    checked={data.length > 0 && selectedCompanies.length === data.length}
                    onChange={handleSelectAll}
                />
            ),
            accessor: company => (
                <input
                    type="checkbox"
                    checked={selectedCompanies.includes(company._id)}
                    onChange={() => handleSelectCompany(company._id)}
                />
            ),
            sortable: false,
            filterable: false,
            width: 50
        },
        {
            id: 'cName',
            Header: 'Name',
            accessor: company => company['cName'] ? company['cName'] : '-',
        },
        {
            id: 'creatorId',
            Header: 'Owner',
            accessor: company => company['creatorId']?.['firstName'] ? company['creatorId']?.['firstName'] + " " + (company['creatorId']?.['lastName'] ? company['creatorId']?.['lastName'] : '') : '-',
        },
        {
            id: 'partnerId',
            Header: 'Partner',
            accessor: company => company['partnerId']?.['name'] ? company['partnerId']?.['name'] : '-',
        },
        {
            id: 'members',
            Header: 'Members',
            accessor: company => company['members']?.length > 0 ?
                company['members']?.map((obj, i, arr) => { return `${obj?.firstName ? obj?.firstName : ""} ${obj?.lastName ? obj?.lastName : ""}` }) : '-',
        },
        {
            id: 'createdAt',
            Header: 'Created At',
            accessor: company => company['createdAt'] ? moment(company['createdAt']).format('DD/MMM/YY') : '-',
        },
        {
            id: 'subscription',
            Header: 'Subscription',
            accessor: company => company['subscription'] ? company['subscription'] : '-',
        },
        {
            id: 'cType',
            Header: 'Type',
            accessor: company => company['cType'] ? company['cType'] : '-',
        },
        {
            id: 'cStage',
            Header: 'Stage',
            accessor: company => company['cStage'] ? company['cStage'] : '-',
        },
        {
            id: 'cLevel',
            Header: 'Certification',
            accessor: company => company['cLevel'] && company['cLevel'] !== 'None' ? company['cLevel'] : 'Uncertified',
        },
        {
            id: 'paymentSource',
            Header: 'Source',
            accessor: company => {
                return company['isParent'] ? "In-Direct" : 'Direct'
            },
        },
        {
            id: 'testType',
            Header: 'Test Type',
            accessor: company => company['testType'] ? company['testType'] : '-',
        },
        // Tags column
        {
            id: 'tags',
            Header: 'Tags',
            accessor: company => (
                <div className="df jcc fww gg0-25rem">
                    {company.tags && company.tags.length > 0 ? (
                        company.tags.map((tag, index) => (
                            <span
                                key={index}
                                style={{
                                    backgroundColor: '#e0e7ff',
                                    color: '#3730a3',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    fontWeight: '500'
                                }}
                            >
                                {tag}
                            </span>
                        ))
                    ) : '-'}
                </div>
            ),
            sortable: false,
            width: 150
        },
        {
            id: 'action',
            Header: 'Revenue Lead',
            accessor: (company) => {
                return <input type="checkbox" checked={company?.revenueLeadMode ?? false} onChange={(e) => handleRevenueLeadMode(company, { revenueLeadMode: e.target.checked }, 'company')} />
            },
        },
        {
            id: 'view',
            Header: 'View',
            accessor: company => {
                return (
                    <>
                        <button
                            onClick={() => {
                                dispatch(toggleModal({ open: 'AdminCompanyDetails' }))
                                setSelectedCompany(company)
                            }}
                            style={{
                                backgroundColor: "transparent",
                                border: "none",
                                cursor: "pointer"
                            }}
                        >
                            <i
                                data-tooltip="View Company Details"
                                className="fa fa-eye c4 cp"
                                style={{ fontSize: '20px' }}
                            />
                        </button>
                    </>
                );
            },
        },
        {
            id: 'delete',
            Header: 'Actions',
            accessor: company => <div className="df aic jcc" style={{ gap: '4px' }}>
                {company['loginLink'] && <button onClick={() => { navigator.clipboard.writeText(company['loginLink']); showMessage('Login link copied!', 'success'); }} style={{ backgroundColor: "transparent", border: "none", cursor: "pointer" }}>
                    <i data-tooltip="Copy Login Link" className="fa fa-copy cp" style={{ fontSize: '20px', color: "#4A67FF" }} />
                </button>}
                <LoginHistoryCell company={company} />
                <button onClick={() => { setDeleteCompany(company); dispatch(toggleModal({ open: 'deleteCompanyPopup' })); }} style={{ backgroundColor: "transparent", border: "none", cursor: "pointer" }}>
                    <i data-tooltip="Delete Company" className="fa fa-trash c9 cp" style={{ fontSize: '20px' }} />
                </button>
            </div>,
            sortable: false,
            width: 130,
        }
    ];

    // Fetch data from server when table changes
    // NOTE: ReactTable calls onFetchData(state, instance) — second arg is the table instance, NOT search
    const fetchData = async (state, searchOverride) => {
        const fetchId = ++fetchIdRef.current;
        setLoading(true);
        setTableState(state);

        // Only use searchOverride if it's explicitly a string (manual calls pass a string)
        const searchValue = typeof searchOverride === 'string' ? searchOverride : searchTermRef.current;

        try {
            const params = {
                page: Math.max(1, (state.page || 0) + 1),
                limit: state.pageSize,
                search: String(searchValue || ""),
                sortField: state.sorted?.[0]?.id || "createdAt",
                sortOrder: state.sorted?.[0]?.asc ? "asc" : "desc",
            };

            // Add tags filter to URL if tags are selected
            const tagsParam = filterTags.length > 0 ? `&tags=${encodeURIComponent(filterTags.join(','))}` : '';

            const companiesResponse = await getRequest(
                `/company/getAllCompanies?page=${params.page}&limit=${params.limit}&search=${encodeURIComponent(
                    params.search
                )}&sortField=${params.sortField}&sortOrder=${params.sortOrder}&isArchived=${isArchived}&yopmailOnly=${isYopmail || false}${tagsParam}`
            );

            // Ignore stale responses from older requests
            if (fetchId !== fetchIdRef.current) return;

            if (companiesResponse && companiesResponse.code === 200) {
                const { allCompanies, pagination } = companiesResponse.body;
                setData(allCompanies || []);
                setPages(pagination?.totalPages || 0);
                // Clear selection when data changes
                setSelectedCompanies([]);
            }
        } catch (error) {
            console.error("Error fetching companies:", error);
            if (fetchId !== fetchIdRef.current) return;
        }

        setLoading(false);
    };

    // Handle tag filter change
    const handleFilterTagToggle = (tag) => {
        setFilterTags(prev => {
            if (prev.includes(tag)) return prev.filter(t => t !== tag);
         else return [...prev, tag];    
        });
    };

    // Apply tag filter
    useEffect(() => {
        fetchData(tableState);
    }, [filterTags]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        searchTermRef.current = value;
        if (value === '') {
            fetchData({ page: 0, pageSize: 20, filtered: [], sorted: [] }, '');
        }
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        fetchData({ page: 0, pageSize: 20, filtered: [], sorted: [] });
    };

    return (
        <>
            <div className="bg1 shadow p2rem dg gg2rem">
                <div className="df fdc aic bss bw2px bo5 br10px p2rem w100 oa">
                    <h3 className="fs1-5rem fs2rem-sm ttu tac c4">
                        {isYopmail ? 'Yopmail' : isArchived ? 'Archived' : 'Subscribed'} Companies Database
                    </h3>

                    {/* Search Bar and Tag Button */}
                    <div className="w100 df jcsb aic mt1rem fww gg1rem">
                        <div className="df aic gg1rem">
                            <form onSubmit={handleSearchSubmit} className="df">
                                <div className="df" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                                    <input type="text" placeholder="Search companies by name, type, or stage..." value={searchTerm} onChange={handleSearchChange} className="py1rem px1-5rem bss bw2px bo5" style={{ fontSize: '1rem', outline: 'none', backgroundColor: '#f8f9fa', transition: 'all 0.3s ease', width: '350px', borderRadius: '12px' }}/>
                                </div>
                            </form>

                            {/* Filter by Tags Dropdown */}
                            <div className="pr">
                                <button onClick={() => setShowFilterDropdown(!showFilterDropdown)} className="cp tac br5px shadow px1rem py0-5rem fw500 df aic" style={{ backgroundColor: filterTags.length > 0 ? '#e0e7ff' : '#f3f4f6', color: filterTags.length > 0 ? '#3730a3' : '#374151', border: filterTags.length > 0 ? '1px solid #c7d2fe' : '1px solid #d1d5db'}} >
                                    Filter by Tags
                                    {filterTags.length > 0 && (
                                        <span style={{ marginLeft: '6px', backgroundColor: '#3730a3', color: 'white', padding: '2px 6px', borderRadius: '10px', fontSize: '11px' }}>
                                            {filterTags.length}
                                        </span>
                                    )}
                                    <span style={{ marginLeft: '8px', fontSize: '10px' }}>{showFilterDropdown ? '▲' : '▼'}</span>
                                </button>

                                {showFilterDropdown && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            marginTop: '8px',
                                            backgroundColor: 'white',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                                            minWidth: '220px',
                                            zIndex: 1000,
                                            border: '1px solid #e5e7eb'
                                        }}
                                    >
                                        <div style={{
                                            padding: '12px 16px',
                                            borderBottom: '1px solid #e5e7eb',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}>
                                            <span style={{ fontWeight: 600, color: '#374151' }}>Filter by Tags</span>
                                            <button onClick={() => { setShowFilterDropdown(false); setFilterSearchTerm(""); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#374151', fontSize: '16px' }}>
                                                ✕
                                            </button>
                                        </div>

                                        {/* Search Bar */}
                                        {allTags.length > 0 && (
                                            <div style={{ padding: '8px 16px', borderBottom: '1px solid #e5e7eb' }}>
                                                <input type="text" placeholder="Search tags..." value={filterSearchTerm} onChange={(e) => setFilterSearchTerm(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', outline: 'none' }} />
                                            </div>
                                        )}

                                        <div style={{ maxHeight: '200px', overflowY: 'auto', padding: '8px 0' }}>
                                            {allTags.length === 0 ? (
                                                <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
                                                    No tags available
                                                </div>
                                            ) : filteredFilterTags.length === 0 ? (
                                                <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
                                                    No tags found
                                                </div>
                                            ) : (
                                                filteredFilterTags.map((tag, index) => (
                                                    <div key={index} onClick={() => handleFilterTagToggle(tag)} style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', cursor: 'pointer', backgroundColor: filterTags.includes(tag) ? '#f0f9ff' : 'transparent' }}>
                                                        <div style={{ width: '18px', height: '18px', borderRadius: '4px', border: `2px solid ${filterTags.includes(tag) ? '#3b82f6' : '#d1d5db'}`, backgroundColor: filterTags.includes(tag) ? '#3b82f6' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '12px' }}>
                                                            {filterTags.includes(tag) && (
                                                                <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold', lineHeight: 1 }}>✓</span>
                                                            )}
                                                        </div>
                                                        <span style={{ color: '#374151', fontSize: '14px' }}>{tag}</span>
                                                    </div>
                                                ))
                                            )}
                                        </div>

                                        {filterTags.length > 0 && (
                                            <div style={{ padding: '8px 16px', borderTop: '1px solid #e5e7eb' }}>
                                                <button
                                                    onClick={() => setFilterTags([])}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px',
                                                        backgroundColor: '#fee2e2',
                                                        color: '#dc2626',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontSize: '13px',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    Clear Filters
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Tag Button - Shows when companies are selected */}
                        <div className="df aic gg1rem pr">
                            {selectedCompanies.length > 0 && (
                                <span className="fs0-875rem c4">
                                    {selectedCompanies.length} selected
                                </span>
                            )}
                            <button
                                onClick={() => setShowTagDropdown(!showTagDropdown)}
                                disabled={selectedCompanies.length === 0}
                                className={`cp tac br5px shadow px1rem py0-5rem fw500 ${selectedCompanies.length > 0 ? 'bg4 c1' : 'bg5 c6'}`}
                                style={{
                                    cursor: selectedCompanies.length > 0 ? 'pointer' : 'not-allowed',
                                    opacity: selectedCompanies.length > 0 ? 1 : 0.5
                                }}
                            >
                                Add Tag
                            </button>

                            {showTagDropdown && (
                                <TagDropdown
                                    allTags={allTags}
                                    selectedCompanies={selectedCompanies}
                                    companiesData={data.filter(c => selectedCompanies.includes(c._id))}
                                    onAddTags={(tags, keepOpen) => handleTagAction('add', tags, keepOpen)}
                                    onRemoveTags={(tags) => handleTagAction('remove', tags)}
                                    onClose={() => setShowTagDropdown(false)}
                                    onTagCreated={fetchAllTags}
                                />
                            )}
                        </div>
                    </div>

                    <div className="admin-table mt1rem w100 ttc tac">
                        <ReactTable
                            manual
                            data={data}
                            pages={pages}
                            loading={loading}
                            columns={companiesColumns}
                            defaultPageSize={20}
                            page={Math.max(0, tableState.page || 0)}
                            onFetchData={fetchData}
                            minRows={0}
                            className="-striped -highlight"
                        />
                    </div>
                </div>
            </div>
            <AdminCompanyDetailsModal id={selectedCompany?._id} setPlan={setPlan} />

            <SubscriptionPaymentConfirm
                plan={plan}
                defaultStep={plan === "Free" ? "freeMembership" : "subscriptionUpgrade"}
                company={selectedCompany}
                companyId={selectedCompany?._id}
                navigateTo={'AdminCompanyDetails'}
                onCancel={() => dispatch(toggleModal({ open: 'AdminCompanyDetails' }))}
            />
        </>
    );
};

export default SubscribedCompaniesTab;
